import React from 'react';
import { useTranslation } from 'react-i18next';
import AppIcon from '../../../../components/AppIcon';
import ReflectionPromptCard from './ReflectionPromptCard';
import type { ReflectionData } from '../types';

interface PromptCardListProps {
  reflectionData: ReflectionData;
  hasManagerResponse: boolean;
  canSubmit: boolean;
  deadlinePassed: boolean;
  onSave: (promptId: number, response: string) => Promise<void>;
  onSubmit: (promptId: number, response: string) => Promise<void>;
}

/** The ONE prompt-card map. Renders however many prompts the company has — three or nine, or none. */
const PromptCardList: React.FC<PromptCardListProps> = ({
  reflectionData, hasManagerResponse, canSubmit, deadlinePassed, onSave, onSubmit,
}) => {
  const { t } = useTranslation();

  // Every prompt switched off by HR: say so, and say who can change it. Never "complete".
  if (reflectionData.prompts.length === 0) {
    return (
      <div className="border border-border rounded-xl bg-card p-8 text-center" data-testid="reflection-no-prompts">
        <AppIcon name="BookOpen" size={28} className="mx-auto mb-3 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">{t('performance.reflections.noActivePrompts')}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t('performance.reflections.noActivePromptsHint')}</p>
      </div>
    );
  }

  return (
  <>
    {reflectionData.prompts.map((prompt, index) => {
      const isSubmitted = reflectionData.submittedPrompts.includes(prompt.id);
      return (
        <ReflectionPromptCard
          key={prompt.id}
          prompt={prompt}
          response={reflectionData.responses[prompt.id] || ''}
          isSubmitted={isSubmitted}
          onSave={onSave}
          onSubmit={onSubmit}
          index={index}
          totalPrompts={reflectionData.prompts.length}
          canSave={!deadlinePassed && !hasManagerResponse}
          canSubmit={canSubmit && !isSubmitted && !hasManagerResponse}
          deadlinePassed={deadlinePassed}
        />
      );
    })}
  </>
  );
};

export default PromptCardList;
